import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { designShowcaseService } from "../services/designShowcaseService";
import {
  DesignShowcaseValuesInit,
  getDesignShowcaseUpdateVariables,
  getDesignShowcaseValues,
  DesignShowcaseValuesType,
  DesignShowcaseErrorsType,
  designShowcaseDisplayId,
} from "./DesignShowcaseCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { DesignShowcase, SortOrder } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getShowcaseCategoryValues,
  ShowcaseCategoryValuesType,
  showcaseCategoryDisplayId,
} from "../../showcaseCategory/store/ShowcaseCategoryCommonStore";
import { isNil } from "../../../common/utils";
import { validateMask } from "../../common/utils";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { imageCache } from "../../../common/services/imageCache";
import { ShowcaseLineUpdateStore } from "../../showcaseLine/store";
import { uploadFailureReason } from "../../../common/utils";
import { apiUploadFile } from "../../../api/apiClient";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { GridChangeSet } from "../../common/types";
import { confirm } from "../../../common/services/confirmService";
import { getApiAsync } from "../../../api";
import { ShowcaseStatus } from "../../../api/graphql";
import { restoredStatus } from "../../common/types";
import { actualStatusList } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { showcaseCategoryService } from "../../showcaseCategory/services/showcaseCategoryService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class DesignShowcaseUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: DesignShowcaseErrorsType = {};
  service = designShowcaseService;
  designShowcaseInstance: DesignShowcase | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    identification: { open: true, errors: [] },
    classification: { open: true, errors: [] },
    numbers: { open: true, errors: [] },
    dates: { open: true, errors: [] },
    flags: { open: true, errors: [] },
    contact: { open: true, errors: [] },
    documents: { open: true, errors: [] },
    richContent: { open: true, errors: [] },
    advanced: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = DesignShowcaseValuesInit();
  lifecycleTransitionProcessing: boolean = false;
  codeError: string | undefined;
  nameError: string | undefined;
  summaryError: string | undefined;
  statusError: string | undefined;
  categoryError: string | undefined;
  categoryService = showcaseCategoryService;
  categorySearchValue: string = "";
  categoryOptions: ShowcaseCategoryValuesType[] = [];
  relatedCategoriesError: string | undefined;
  relatedCategoriesService = showcaseCategoryService;
  relatedCategoriesSearchValue: string = "";
  relatedCategoriesOptions: ShowcaseCategoryValuesType[] = [];
  appliedStatusesError: string | undefined;
  quantityError: string | undefined;
  durationInYearsError: string | undefined;
  amountError: string | undefined;
  ratioError: string | undefined;
  startDateError: string | undefined;
  expiresAtError: string | undefined;
  registeredAtError: string | undefined;
  activeError: string | undefined;
  highlightedError: string | undefined;
  phoneError: string | undefined;
  taxIdError: string | undefined;
  attachmentError: string | undefined;
  anyFileError: string | undefined;
  avatarError: string | undefined;
  avatarPreviewUrl: string | null = null;
  keywordsError: string | undefined;
  bodyError: string | undefined;
  metadataError: string | undefined;
  internalNoteError: string | undefined;
  apiKeyError: string | undefined;
  linesError: string | undefined;
  linesVisibleColumns: { name: string; title: string }[];
  linesColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  linesUpdateStore: ShowcaseLineUpdateStore;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<DesignShowcaseValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.linesVisibleColumns = [
      { name: "concept", title: this.langStore.ShowcaseLineConceptLabel },
      { name: "quantity", title: this.langStore.ShowcaseLineQuantityLabel },
      { name: "unitPrice", title: this.langStore.ShowcaseLineUnitPriceLabel },
    ];
    this.linesColumnTypes = {
      concept: {
        fieldType: "String" as const,
        nullable: false,
        readOnly: false,
      },
      quantity: { fieldType: "Int" as const, nullable: false, readOnly: false },
      unitPrice: {
        fieldType: "Decimal" as const,
        nullable: false,
        readOnly: false,
      },
    };
    this.linesUpdateStore = new ShowcaseLineUpdateStore(
      rootStore,
      "lines_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<DesignShowcaseValuesType>
        );
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }

    if (this.linesUpdateStore) {
      this.linesUpdateStore.destroy();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<DesignShowcaseValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadDesignShowcaseById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.designShowcaseInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.designShowcaseInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getDesignShowcaseValues(
        this.designShowcaseInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadDesignShowcase(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() => this.loadDesignShowcaseById(this.entityValues.id));
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return designShowcaseDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.designShowcaseInstance) {
      return designShowcaseDisplayId(
        this.designShowcaseInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.DesignShowcaseSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  viewFormCategory() {
    const id = this.entityValues.showCategory?.id;
    if (id) {
      this.rootStore.openOrPushOverlay("showcaseCategory", id, "view");
    }
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupDesignShowcaseLabel,
        errors: this.getCardErrors("null"),
      },
      {
        id: "identification",
        label: this.rootStore.langStore.groupDesignShowcaseIdentificationLabel,
        errors: this.getCardErrors("identification"),
      },
      {
        id: "classification",
        label: this.rootStore.langStore.groupDesignShowcaseClassificationLabel,
        errors: this.getCardErrors("classification"),
      },
      {
        id: "numbers",
        label: this.rootStore.langStore.groupDesignShowcaseNumbersLabel,
        errors: this.getCardErrors("numbers"),
      },
      {
        id: "dates",
        label: this.rootStore.langStore.groupDesignShowcaseDatesLabel,
        errors: this.getCardErrors("dates"),
      },
      {
        id: "flags",
        label: this.rootStore.langStore.groupDesignShowcaseFlagsLabel,
        errors: this.getCardErrors("flags"),
      },
      {
        id: "contact",
        label: this.rootStore.langStore.groupDesignShowcaseContactLabel,
        errors: this.getCardErrors("contact"),
      },
      {
        id: "documents",
        label: this.rootStore.langStore.groupDesignShowcaseDocumentsLabel,
        errors: this.getCardErrors("documents"),
      },
      {
        id: "richContent",
        label: this.rootStore.langStore.groupDesignShowcaseRichContentLabel,
        errors: this.getCardErrors("richContent"),
      },
      {
        id: "advanced",
        label: this.rootStore.langStore.groupDesignShowcaseAdvancedLabel,
        errors: this.getCardErrors("advanced"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity
      if (callerData?.entityName === "ShowcaseLine") {
        this.linesUpdateStore.isWaitingForCallback = false;
        this.linesUpdateStore.callbackReturnPath = null;
        yield this.linesUpdateStore.returnedData(data);
        return;
      }

      if (callerData?.fieldName === "category") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            showcaseCategoryDisplayId(returnData.entity, this.langStore);
          this.entityValues.showCategory = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.showcaseCategoryOne({
            id: returnData.resultId,
          });
          const showcaseCategory = result.showcaseCategoryOne;
          this.entityValues.showCategory = {
            displayId: showcaseCategory.displayId,
            id: showcaseCategory.id,
          };
          this.setCategoryOptions([
            getShowcaseCategoryValues(showcaseCategory, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "relatedCategories") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            showcaseCategoryDisplayId(returnData.entity, this.langStore);
          this.entityValues.showRelatedCategories.push({
            id: returnData.entity.id,
            displayId,
          });
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.showcaseCategoryOne({
            id: returnData.resultId,
          });
          const showcaseCategory = result.showcaseCategoryOne;
          this.entityValues.showRelatedCategories.push({
            displayId: showcaseCategory.displayId,
            id: showcaseCategory.id,
          });
          this.setRelatedCategoriesOptions([
            getShowcaseCategoryValues(showcaseCategory, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  *showcaseWorkflow_Submit(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result = yield this.service.showcaseWorkflow_Submit(
        this.entityValues.id,
        this.entityValues.version_,
        "DesignShowcase",
        validToken,
        this.rootStore.activityStore
      );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.lifecycle_transitions_ = result.nextTransitions || [];
        yield this.loadDesignShowcaseById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *showcaseWorkflow_Approve(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result = yield this.service.showcaseWorkflow_Approve(
        this.entityValues.id,
        this.entityValues.version_,
        "DesignShowcase",
        validToken,
        this.rootStore.activityStore
      );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.lifecycle_transitions_ = result.nextTransitions || [];
        yield this.loadDesignShowcaseById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *showcaseWorkflow_Reject(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result = yield this.service.showcaseWorkflow_Reject(
        this.entityValues.id,
        this.entityValues.version_,
        "DesignShowcase",
        validToken,
        this.rootStore.activityStore
      );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.lifecycle_transitions_ = result.nextTransitions || [];
        yield this.loadDesignShowcaseById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *showcaseWorkflow_Fail(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result = yield this.service.showcaseWorkflow_Fail(
        this.entityValues.id,
        this.entityValues.version_,
        "DesignShowcase",
        validToken,
        this.rootStore.activityStore
      );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.lifecycle_transitions_ = result.nextTransitions || [];
        yield this.loadDesignShowcaseById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *showcaseWorkflow_Retry(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result = yield this.service.showcaseWorkflow_Retry(
        this.entityValues.id,
        this.entityValues.version_,
        "DesignShowcase",
        validToken,
        this.rootStore.activityStore
      );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.lifecycle_transitions_ = result.nextTransitions || [];
        yield this.loadDesignShowcaseById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *showcaseWorkflow_Close(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result = yield this.service.showcaseWorkflow_Close(
        this.entityValues.id,
        this.entityValues.version_,
        "DesignShowcase",
        validToken,
        this.rootStore.activityStore
      );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.lifecycle_transitions_ = result.nextTransitions || [];
        yield this.loadDesignShowcaseById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *showcaseWorkflow_Reopen(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result = yield this.service.showcaseWorkflow_Reopen(
        this.entityValues.id,
        this.entityValues.version_,
        "DesignShowcase",
        validToken,
        this.rootStore.activityStore
      );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.lifecycle_transitions_ = result.nextTransitions || [];
        yield this.loadDesignShowcaseById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *onLifecycleTransition(transitionName: string) {
    if (this.pendingChanges) {
      this.showErrorDialog(
        undefined,
        this.langStore.cannotProceedWithChanges,
        this.langStore.saveChangesFirst
      );
      return;
    }
    this.lifecycleTransitionProcessing = true;
    try {
      const transitionLabel =
        this.langStore.DesignShowcaseLifecycleTransitionLabels[transitionName];
      yield this.executeLifecycleTransition(transitionName);
      if (!this.errorDialogOpened) {
        const newStateId = this.entityValues.showLifecycle?.id || "";
        const stateLabel =
          this.langStore.DesignShowcaseLifecycleStateLabels[newStateId] || "";
        const message = `${transitionLabel}. ${this.langStore.workflowNewStatePrefix}: ${stateLabel}`;
        this.rootStore.showSnackbar(message, "success");
      }
    } finally {
      this.lifecycleTransitionProcessing = false;
    }
  }

  *executeLifecycleTransition(transitionName: string) {
    if (transitionName === "submit") {
      yield this.showcaseWorkflow_Submit();
    }
    if (transitionName === "approve") {
      yield this.showcaseWorkflow_Approve();
    }
    if (transitionName === "reject") {
      yield this.showcaseWorkflow_Reject();
    }
    if (transitionName === "fail") {
      yield this.showcaseWorkflow_Fail();
    }
    if (transitionName === "retry") {
      yield this.showcaseWorkflow_Retry();
    }
    if (transitionName === "close") {
      yield this.showcaseWorkflow_Close();
    }
    if (transitionName === "reopen") {
      yield this.showcaseWorkflow_Reopen();
    }
  }

  get lifecycleTransitions() {
    return (this.entityValues.lifecycle_transitions_ || []).map((x) => {
      return {
        id: x,
        label: this.langStore.DesignShowcaseLifecycleTransitionLabels[x],
      };
    });
  }

  getCodeValue(): string {
    return this.entityValues.code;
  }

  setCodeValue(value: string) {
    this.entityValues.code = value;
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  getSummaryValue(): string {
    return this.entityValues.summary;
  }

  setSummaryValue(value: string) {
    this.entityValues.summary = value;
  }

  getStatusValue(): ShowcaseStatus {
    return this.entityValues.status;
  }

  setStatusValue(value: ShowcaseStatus) {
    this.entityValues.status = value;
  }

  getStatusOptions() {
    return this.langStore.ShowcaseStatusOptions;
  }

  setCategoryOptions(values: ShowcaseCategoryValuesType[]) {
    this.categoryOptions = values;
  }

  *loadCategoryValues(): any {
    let where: any = null;
    if (this.categorySearchValue) {
      const searchFilter = {
        name: { contains: this.categorySearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.showcaseCategoryListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ name: SortOrder.Asc }],
    });
    this.setCategoryOptions(
      (result.showcaseCategoryListWithOffset || []).map((x: any) =>
        getShowcaseCategoryValues(x, this.langStore)
      )
    );
  }

  getCategoryOptions() {
    return this.categoryOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setCategoryValue(value: string) {
    this.categorySearchValue = value;
    yield this.loadCategoryValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedCategory(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewShowcaseCategory(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "category"
      );
    } else {
      if (value) {
        this.entityValues.showCategory = value;
        this.entityValues.__category = value.displayId;
      }
    }
  }

  setRelatedCategoriesOptions(values: ShowcaseCategoryValuesType[]) {
    this.relatedCategoriesOptions = values;
  }

  *loadRelatedCategoriesValues(): any {
    let where: any = null;
    if (this.relatedCategoriesSearchValue) {
      const searchFilter = {
        name: {
          contains: this.relatedCategoriesSearchValue,
          mode: "insensitive",
        },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.showcaseCategoryListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ name: SortOrder.Asc }],
    });
    this.setRelatedCategoriesOptions(
      (result.showcaseCategoryListWithOffset || []).map((x: any) =>
        getShowcaseCategoryValues(x, this.langStore)
      )
    );
  }

  getRelatedCategoriesOptions() {
    return this.relatedCategoriesOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setRelatedCategoriesValue(value: string) {
    this.relatedCategoriesSearchValue = value;
    yield this.loadRelatedCategoriesValues();
  }

  setSelectedRelatedCategories(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewShowcaseCategory(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "relatedCategories"
      );
    } else {
      this.entityValues.showRelatedCategories = values;
      this.entityValues.__relatedCategories = values.map((v) => v.displayId);
    }
  }

  getAppliedStatusesValue(): ShowcaseStatus[] {
    return this.entityValues.appliedStatuses;
  }

  setAppliedStatusesValue(value: ShowcaseStatus[]) {
    this.entityValues.appliedStatuses = value;
  }

  getAppliedStatusesOptions() {
    return this.langStore.ShowcaseStatusOptions;
  }

  getQuantityValue(): number {
    return this.entityValues.quantity;
  }

  setQuantityValue(value: number) {
    this.entityValues.quantity = value;
  }

  getDurationInYearsValue(): number | undefined {
    return this.entityValues.durationInYears;
  }

  setDurationInYearsValue(value: number | undefined) {
    this.entityValues.durationInYears = value;
  }

  getAmountValue(): number {
    return this.entityValues.amount;
  }

  setAmountValue(value: number) {
    this.entityValues.amount = value;
  }

  getRatioValue(): number | undefined {
    return this.entityValues.ratio;
  }

  setRatioValue(value: number | undefined) {
    this.entityValues.ratio = value;
  }

  getStartDateValue(): string | null {
    return this.entityValues.startDate;
  }

  setStartDateValue(value: string | null) {
    if (value !== null) {
      this.entityValues.startDate = value;
    }
  }

  getExpiresAtValue(): Date | null {
    return this.entityValues.expiresAt;
  }

  setExpiresAtValue(value: Date | null) {
    this.entityValues.expiresAt = value;
  }

  getRegisteredAtValue(): Date | null {
    return this.entityValues.registeredAt;
  }

  setRegisteredAtValue(value: Date | null) {
    this.entityValues.registeredAt = value;
  }

  getActiveValue(): boolean {
    return this.entityValues.active;
  }

  setActiveValue(value: boolean) {
    this.entityValues.active = value;
  }

  getHighlightedValue(): boolean {
    return this.entityValues.highlighted;
  }

  setHighlightedValue(value: boolean) {
    this.entityValues.highlighted = value;
  }

  getPhoneValue(): string {
    return this.entityValues.phone;
  }

  setPhoneValue(value: string) {
    this.entityValues.phone = value;
  }

  getTaxIdValue(): string {
    return this.entityValues.taxId;
  }

  setTaxIdValue(value: string) {
    this.entityValues.taxId = value;
  }

  attachmentRestoreFile(id: string) {
    const file = this.entityValues.__attachment_files.find((x) => x.id === id);
    if (!file) return;

    const newStatus = restoredStatus(file.__status__);

    const activeFiles = this.entityValues.__attachment_files.filter((x) =>
      actualStatusList.includes(x.__status__)
    );
    if (activeFiles.length >= 1 && newStatus !== file.__status__) {
      alert("No puede haber más de un archivo activo");
      return;
    }

    file.__status__ = newStatus;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  *attachmentUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__attachment_files];
    const previousName = this.entityValues.attachment;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__attachment_files.push(newFile);
    this.entityValues.attachment = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__attachment_files = previousFiles;
      this.entityValues.attachment = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("attachment");
      } else {
        this.setFieldError(
          "attachment",
          this.langStore.DesignShowcaseAttachmentLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("attachment");
    const fileIndex = this.entityValues.__attachment_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__attachment_files[fileIndex].ticket = outcome.ticket;
    }
  }

  attachmentCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__attachment_files) {
        if (file.id === id) {
          if (isOriginalCrudStatus(file.__status__)) {
            file.__status__ = "original-deleted";
          } else {
            toRemove = index;
          }
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__attachment_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  anyFileRestoreFile(id: string) {
    const file = this.entityValues.__anyFile_files.find((x) => x.id === id);
    if (!file) return;

    const newStatus = restoredStatus(file.__status__);

    const activeFiles = this.entityValues.__anyFile_files.filter((x) =>
      actualStatusList.includes(x.__status__)
    );
    if (activeFiles.length >= 1 && newStatus !== file.__status__) {
      alert("No puede haber más de un archivo activo");
      return;
    }

    file.__status__ = newStatus;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  *anyFileUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__anyFile_files];
    const previousName = this.entityValues.anyFile;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__anyFile_files.push(newFile);
    this.entityValues.anyFile = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__anyFile_files = previousFiles;
      this.entityValues.anyFile = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("anyFile");
      } else {
        this.setFieldError(
          "anyFile",
          this.langStore.DesignShowcaseAnyFileLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("anyFile");
    const fileIndex = this.entityValues.__anyFile_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__anyFile_files[fileIndex].ticket = outcome.ticket;
    }
  }

  anyFileCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__anyFile_files) {
        if (file.id === id) {
          if (isOriginalCrudStatus(file.__status__)) {
            file.__status__ = "original-deleted";
          } else {
            toRemove = index;
          }
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__anyFile_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  *avatarUploadBlob(blob: Blob): any {
    const previousFiles = [...this.entityValues.__avatar_files];
    const previousName = this.entityValues.avatar;
    // Crear preview URL para mostrar inmediatamente
    if (this.avatarPreviewUrl) {
      URL.revokeObjectURL(this.avatarPreviewUrl);
    }
    this.avatarPreviewUrl = URL.createObjectURL(blob);

    const file = new File([blob], "avatar.jpg", {
      type: blob.type || "image/jpeg",
    });
    const fileId = `avatar_${Date.now()}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__avatar_files = [newFile];
    this.entityValues.avatar = file.name;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__avatar_files = previousFiles;
      this.entityValues.avatar = previousName;
      if (this.avatarPreviewUrl) {
        URL.revokeObjectURL(this.avatarPreviewUrl);
        this.avatarPreviewUrl = null;
      }
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("avatar");
      } else {
        this.setFieldError(
          "avatar",
          this.langStore.DesignShowcaseAvatarLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("avatar");
    const fileIndex = this.entityValues.__avatar_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__avatar_files[fileIndex].ticket = outcome.ticket;
    }
  }

  avatarDelete() {
    // Limpiar preview URL
    if (this.avatarPreviewUrl) {
      URL.revokeObjectURL(this.avatarPreviewUrl);
      this.avatarPreviewUrl = null;
    }

    const existingFile = this.entityValues.__avatar_files.find(
      (f) => f.__status__ === "original"
    );
    if (existingFile) {
      existingFile.__status__ = "original-deleted";
    }
    this.entityValues.__avatar_files = this.entityValues.__avatar_files.filter(
      (f) => f.__status__ !== "added"
    );
    this.entityValues.avatar = "";
  }

  getKeywordsValue(): string[] {
    return this.entityValues.keywords;
  }

  setKeywordsValue(value: string[]) {
    this.entityValues.keywords = value;
  }

  getBodyValue(): string {
    return this.entityValues.body;
  }

  setBodyValue(value: string) {
    this.entityValues.body = value;
  }

  getMetadataValue(): string {
    return this.entityValues.metadata;
  }

  setMetadataValue(value: string) {
    this.entityValues.metadata = value;
  }

  getInternalNoteValue(): string {
    return this.entityValues.internalNote;
  }

  setInternalNoteValue(value: string) {
    this.entityValues.internalNote = value;
  }

  getApiKeyValue(): string {
    return this.entityValues.apiKey;
  }

  setApiKeyValue(value: string) {
    this.entityValues.apiKey = value;
  }

  private get lineshasChanges() {
    return (
      this.entityValues.showLines?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  linesLoadRow(row: any) {
    this.linesUpdateStore.loadValuesFromRow(row);
  }

  linesRestore(id: string) {
    this.entityValues.showLines = this.entityValues.showLines.map((item) =>
      item.id === id
        ? { ...item, __status__: restoredStatus(item.__status__) }
        : item
    );
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  linesValidateChanges(_changes: GridChangeSet) {
    const errors = this.linesUpdateStore.validateData(
      this.linesUpdateStore.entityValues
    );

    this.linesUpdateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  linesCommitInlineChange(rowId: string, changes: Record<string, any>) {
    this.entityValues.showLines = this.entityValues.showLines.map((item) =>
      item.id === rowId
        ? { ...item, ...changes, __status__: updatedStatus(item.__status__) }
        : item
    );
  }

  linesCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.linesUpdateStore.entityValues;
      const hadChanges = this.linesUpdateStore.pendingChanges;
      if (hadChanges) {
        const lines = this.entityValues.showLines;
        for (
          let index = 0;
          index < this.entityValues.showLines.length;
          index++
        ) {
          if (lines[index].id === values.id) {
            this.entityValues.showLines = this.entityValues.showLines.map(
              (item, idx) =>
                idx === index
                  ? { ...values, __status__: updatedStatus(values.__status__) }
                  : item
            );
            break;
          }
        }
        this.linesUpdateStore.clearFields();
      }
    } else if (changes.added) {
      const values = this.linesUpdateStore.entityValues;
      const newId = `${this.uniqueCounter++}`;
      if (!this.entityValues.showLines) {
        this.entityValues.showLines = [];
      }
      this.entityValues.showLines = [
        ...this.entityValues.showLines,
        {
          ...values,
          id: newId,
          __status__: "added",
        },
      ];
      this.linesUpdateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const lines = this.entityValues.showLines;
      for (let index = 0; index < this.entityValues.showLines.length; index++) {
        if (lines[index].id === id) {
          if (isOriginalCrudStatus(lines[index].__status__)) {
            this.entityValues.showLines = this.entityValues.showLines.map(
              (item, idx) =>
                idx === index
                  ? { ...item, __status__: deleteStatus(item.__status__) }
                  : item
            );
          } else {
            this.entityValues.showLines = this.entityValues.showLines.filter(
              (x) => x.id !== id
            );
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.lines);
    //   this.setCRUDRowsFromValues(newChanges, this.linesUpdateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.lines = getNewCRUDRows(newChanges, this.entityValues.lines, this.uniqueCounter++);
    // }
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as DesignShowcaseErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as DesignShowcaseErrorsType;
  }

  *fileDownload(props: any, fieldName: string): any {
    // En el modal anidado (subentidad editada desde el parent) el instance
    // no se carga (loadValuesFromRow): el id sale de los values de la fila.
    // Sin el fallback, la URL iba con instanceId=undefined → 404 silencioso.
    const instanceId = this.designShowcaseInstance?.id ?? this.entityValues.id;
    yield downloadWithAuth(
      {
        entityName: "DesignShowcase",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      },
      undefined,
      this.rootStore.activityStore
    );
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      // Mismo fallback que fileDownload: en el modal anidado el instance
      // no se carga y el id viene de los values de la fila.
      const instanceId =
        this.designShowcaseInstance?.id ?? this.entityValues.id;
      const blob = yield fetchFileBlob({
        entityName: "DesignShowcase",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  *addNewShowcaseCategory(
    notFound: string | undefined,
    callerFieldName?: string
  ): any {
    // QuickCreate checks for fields with quickCreate enabled
    if (notFound && callerFieldName === "category") {
      const confirmed = yield confirm(
        this.langStore.quickCreateConfirmTitle(
          this.langStore.ShowcaseCategorySingular
        ),
        this.langStore.quickCreateConfirmDescription(
          notFound,
          this.langStore.ShowcaseCategorySingular
        )
      );
      if (!confirmed) return;

      try {
        const api = yield getApiAsync(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.showcaseCategoryCreate({ name: notFound });

        if (result.showcaseCategoryCreate) {
          this.entityValues.showCategory = {
            id: result.showcaseCategoryCreate.id,
            displayId: result.showcaseCategoryCreate.displayId || notFound,
          };
          this.entityValues.__category =
            result.showcaseCategoryCreate.displayId || notFound;
        }
      } catch (error) {
        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      }
      return;
    }

    // Fallback to navigation for other fields
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "DesignShowcase",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/showcaseCategory/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.designShowcaseInstance = null;
    this.entityValues = DesignShowcaseValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      identification: { open: true, errors: [] },
      classification: { open: true, errors: [] },
      numbers: { open: true, errors: [] },
      dates: { open: true, errors: [] },
      flags: { open: true, errors: [] },
      contact: { open: true, errors: [] },
      documents: { open: true, errors: [] },
      richContent: { open: true, errors: [] },
      advanced: { open: true, errors: [] },
    };
    this.categorySearchValue = "";
    this.categoryOptions = [];
    this.relatedCategoriesSearchValue = "";
    this.relatedCategoriesOptions = [];
    if (this.avatarPreviewUrl) {
      URL.revokeObjectURL(this.avatarPreviewUrl);
      this.avatarPreviewUrl = null;
    }
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: DesignShowcaseValuesType): DesignShowcaseErrorsType {
    const errors: DesignShowcaseErrorsType = {};
    this.clearErrors();
    values.code = values.code.trim();
    if (values.code === "") {
      errors.code = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseCodeLabel,
      };
      this.setCardError("identification", errors.code.message);
    }
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseNameLabel,
      };
      this.setCardError("identification", errors.name.message);
    }

    if (!values.status) {
      errors.status = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseStatusLabel,
      };
      this.setCardError("classification", errors.status.message);
    }
    this.categoryError = undefined;
    if (values.showCategory === null || values.showCategory.id === "") {
      errors.category = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseCategoryLabel,
      };
      this.setCardError("classification", errors.category.message);
    }
    if (values.quantity > 9999) {
      errors.quantity = {
        message: this.langStore.maxValue + ": 9999",
        fieldLabel: this.langStore.DesignShowcaseQuantityLabel,
      };
      this.setCardError("numbers", errors.quantity.message);
    }
    if (values.durationInYears != null && values.durationInYears > 50) {
      errors.durationInYears = {
        message: this.langStore.maxValue + ": 50",
        fieldLabel: this.langStore.DesignShowcaseDurationInYearsLabel,
      };
      this.setCardError("numbers", errors.durationInYears.message);
    }
    if (values.durationInYears != null && values.durationInYears < 1) {
      errors.durationInYears = {
        message: this.langStore.minValue + ": 1",
        fieldLabel: this.langStore.DesignShowcaseDurationInYearsLabel,
      };
      this.setCardError("numbers", errors.durationInYears.message);
    }

    if (isNil(values.amount)) {
      errors.amount = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseAmountLabel,
      };
      this.setCardError("numbers", errors.amount.message);
    }
    if (values.ratio != null && values.ratio > 100) {
      errors.ratio = {
        message: this.langStore.maxValue + ": 100",
        fieldLabel: this.langStore.DesignShowcaseRatioLabel,
      };
      this.setCardError("numbers", errors.ratio.message);
    }

    if (isNil(values.startDate) || values.startDate === "") {
      errors.startDate = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseStartDateLabel,
      };
      this.setCardError("dates", errors.startDate.message);
    }

    if (isNil(values.active)) {
      errors.active = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseActiveLabel,
      };
      this.setCardError("flags", errors.active.message);
    }

    if (isNil(values.highlighted)) {
      errors.highlighted = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.DesignShowcaseHighlightedLabel,
      };
      this.setCardError("flags", errors.highlighted.message);
    }
    if (values.taxId && true) {
      if (!validateMask(values.taxId, { mask: "99-99999999-9" })) {
        errors.taxId = {
          message: this.langStore.maskedField("99-99999999-9"),
          fieldLabel: this.langStore.DesignShowcaseTaxIdLabel,
        };
      }
    }
    {
      const pendingUpload = values.__attachment_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.attachment = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.DesignShowcaseAttachmentLabel,
        };
        this.setCardError("documents", errors.attachment.message);
      }
    }
    {
      const pendingUpload = values.__anyFile_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.anyFile = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.DesignShowcaseAnyFileLabel,
        };
        this.setCardError("documents", errors.anyFile.message);
      }
    }
    {
      const pendingUpload = values.__avatar_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.avatar = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.DesignShowcaseAvatarLabel,
        };
        this.setCardError("documents", errors.avatar.message);
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "DesignShowcaseUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges || this.hasArrayChanges();
  }

  private hasArrayChanges(): boolean {
    return this.lineshasChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      code: this.entityValues.code,
      name: this.entityValues.name,
      summary: this.entityValues.summary,
      status: this.entityValues.status,
      quantity: this.entityValues.quantity,
      durationInYears: this.entityValues.durationInYears,
      amount: this.entityValues.amount,
      ratio: this.entityValues.ratio,
      startDate: this.entityValues.startDate,
      expiresAt: this.entityValues.expiresAt,
      registeredAt: this.entityValues.registeredAt,
      active: this.entityValues.active,
      highlighted: this.entityValues.highlighted,
      phone: this.entityValues.phone,
      taxId: this.entityValues.taxId,
      attachment: this.entityValues.attachment,
      anyFile: this.entityValues.anyFile,
      avatar: this.entityValues.avatar,
      keywords: this.entityValues.keywords,
      body: this.entityValues.body,
      metadata: this.entityValues.metadata,
      internalNote: this.entityValues.internalNote,
      apiKey: this.entityValues.apiKey,
      category: this.entityValues.showCategory?.id || null,
      relatedCategories: (this.entityValues.showRelatedCategories || [])
        .map((x: any) => x.id)
        .sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }
    if (this.lineshasChanges) changed.push("lines");
    return changed;
  }

  *saveDesignShowcase(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // Guardar datos del avatar anterior para invalidar caché después
      const previousAvatarName = this.designShowcaseInstance?.avatar_data?.name;
      const entityId = this.designShowcaseInstance?.id;

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadDesignShowcaseById(this.savedId);
              } catch (error) {
                console.warn(
                  "[DesignShowcaseUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }

            // Limpiar preview URL ya que la imagen está en el servidor
            if (this.avatarPreviewUrl) {
              URL.revokeObjectURL(this.avatarPreviewUrl);
              this.avatarPreviewUrl = null;
            }

            // Invalidar caché de imagen del avatar anterior
            if (previousAvatarName && entityId) {
              imageCache.removeFromCache(
                "DesignShowcase",
                "avatar",
                entityId,
                previousAvatarName
              );
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelDesignShowcase(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getDesignShowcaseValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = DesignShowcaseValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.designShowcaseInstance) {
      const variables = getDesignShowcaseUpdateVariables(this.entityValues);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.designShowcaseUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.designShowcaseUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.designShowcaseUpdate.version_;
      this.saveAttempt = null;
      this.designShowcaseInstance =
        result.designShowcaseUpdate as DesignShowcase;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadDesignShowcaseById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadCategoryValues();
      yield this.loadRelatedCategoriesValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
